// src/App.js
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Testimonials from './components/Testimonials';
import UtilityButtons from './components/UtilityButtons';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AubergePage = lazy(() => import('./pages/AubergePage'));
const GitesPage = lazy(() => import('./pages/GitesPage'));
const LaFermePage = lazy(() => import('./pages/LaFermePage'));
const TarifsPage = lazy(() => import('./pages/TarifsPage'));
const NotreRegionPage = lazy(() => import('./pages/NotreRegionPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const DetailPage = lazy(() => import('./pages/DetailPage'));
const GiteDetailPage = lazy(() => import('./pages/GiteDetailPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const AuthModal = lazy(() => import('./components/auth/AuthModal'));

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { currentUser, isAdmin } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/non-autorise" replace />;
  }
  
  return children;
};


function AppLayout({ showInstallButton, onInstallClick }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      {!isAdminRoute && <Navbar />}
      
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/a-propos" element={<AboutPage />} />
        <Route path="/auberge" element={<AubergePage />} />
        <Route path="/gites" element={<GitesPage />} />
        <Route path="/la-ferme" element={<LaFermePage />} />
        <Route path="/tarifs" element={<TarifsPage />} />
        <Route path="/notre-region" element={<NotreRegionPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/gite/:giteId" element={<GiteDetailPage />} />
        <Route path="/lieu/:slug" element={<DetailPage />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
        
        {/* Auth Routes */}
        <Route path="/connexion" element={<AuthModal isOpen={true} initialMode="login" onClose={() => window.history.back()} />} />
        <Route path="/inscription" element={<AuthModal isOpen={true} initialMode="signup" onClose={() => window.history.back()} />} />
        
        {/* Protected Routes */}
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/mon-compte" 
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Error Routes */}
        <Route path="/non-autorise" element={
          <div className="error-page">
            <h2>Accès non autorisé</h2>
            <p>Vous n'avez pas les droits nécessaires pour accéder à cette page.</p>
          </div>
        } />
        
        <Route path="*" element={
          <div className="error-page">
            <h2>404 - Page non trouvée</h2>
            <p>La page que vous recherchez n'existe pas ou a été déplacée.</p>
          </div>
        } />
      </Routes>

      {!isAdminRoute && <Footer showInstallButton={showInstallButton} onInstallClick={onInstallClick} />}
      {!isAdminRoute && <UtilityButtons />}
    </div>
  );
}

function AppContent() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`Installation : ${outcome}`);
      if (outcome === 'accepted') {
        setShowInstallButton(false);
        setDeferredPrompt(null);
      }
    }
  };

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner fullPage />}>
        <AppLayout showInstallButton={showInstallButton} onInstallClick={handleInstallClick} />
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;